import { useMemo } from 'react';
import { floatUid } from 'utils/uid';
import {
  ProjectTemplateData,
  ProjectTemplateProjectPhase,
  ProjectTemplateTask,
} from '../types';
import { useGetAllProjectPhases } from './use-get-all-project-phases';
import { useGetAllProjectPhasesInTemplate } from './use-get-all-project-phases-in-template';
import { useGetAllTasks } from './use-get-all-tasks';

const frameTaskObject = (task: ProjectTemplateTask, rootIndex: number) => ({
  key: `TASK-${task.taskId}`,
  id: task.taskId,
  title: task.taskName,
  startDay: task.startDay,
  duration: task.duration,
  assignee: task.assignee,
  private: task.private,
  data: task,
  rootIndex,
});

export const useTransformTabularData = (
  phases?: ProjectTemplateProjectPhase[] | null,
): ProjectTemplateData[] => {
  const projectPhaseIds = useGetAllProjectPhasesInTemplate();
  const projectPhases = useGetAllProjectPhases();
  const allTasks = useGetAllTasks();

  return useMemo(() => {
    const _phases =
      phases !== undefined && phases !== null
        ? phases || []
        : (projectPhaseIds ?? []).map((id) => projectPhases[id]);

    return _phases.map((projectPhase, index) => {
      return {
        key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
        id: projectPhase.projectPhaseId,
        title: projectPhase.projectPhaseName,
        startDay: projectPhase.startDay,
        duration: projectPhase.duration,
        type: 'PROJECT_PHASE',
        private: projectPhase.private,
        level: 0,
        data: projectPhase,
        rootIndex: index,
        parentTotal: 0,
        children: projectPhase.tasks.map((taskRef) => {
          const task = taskRef.taskId
            ? taskRef
            : allTasks[taskRef as unknown as number];

          return {
            ...frameTaskObject(task, index),
            type: 'TASK',
            level: 1,
            parentTotal: projectPhase.tasks.length,
            children: task.subTasks?.map((subTaskRef) => {
              const subTask = subTaskRef.taskId
                ? subTaskRef
                : allTasks[subTaskRef as unknown as number];

              return {
                ...frameTaskObject(subTask, index),
                type: 'TASK',
                level: 2,
                parentTotal: task.subTasks.length,
                children: subTask.subTasks?.map((_subTaskRef) => {
                  const _subTask = _subTaskRef.taskId
                    ? _subTaskRef
                    : allTasks[_subTaskRef as unknown as number];

                  return {
                    ...frameTaskObject(_subTask, index),
                    parentTotal: subTask.subTasks.length,
                    level: 3,
                    type: 'TASK',
                    children: [],
                  };
                }),
              };
            }),
          };
        }),
      };
    });
  }, [projectPhaseIds, projectPhases, allTasks, phases]);
};

export const useTransformTabularDataForAgGrid = (
  phases?: ProjectTemplateProjectPhase[],
): ProjectTemplateData[] => {
  const projectPhaseIds = useGetAllProjectPhasesInTemplate();
  const projectPhases = useGetAllProjectPhases();
  const allTasks = useGetAllTasks();

  return useMemo(() => {
    const _phases =
      phases !== undefined && phases !== null
        ? phases || []
        : (projectPhaseIds ?? []).map((id) => projectPhases[id]);

    const result: any[] = [];

    _phases.forEach((projectPhase, index) => {
      const phase = {
        key: `PROJECT_PHASE-${projectPhase.projectPhaseId}`,
        id: projectPhase.projectPhaseId,
        title: projectPhase.projectPhaseName,
        startDay: projectPhase.startDay,
        duration: projectPhase.duration,
        type: 'PROJECT_PHASE',
        private: projectPhase.private,
        level: 0,
        data: projectPhase,
        rootIndex: index,
        parentTotal: 0,
        hierarchy: [projectPhase.projectPhaseId].map(String),
      };

      result.push(phase);
      projectPhase.tasks.forEach((taskRef) => {
        const task = taskRef.taskId
          ? taskRef
          : allTasks[taskRef as unknown as number];

        const phaseTask = {
          ...frameTaskObject(task, index),
          type: 'TASK',
          level: 1,
          parentTotal: projectPhase.tasks.length,
          hierarchy: [projectPhase.projectPhaseId, task.taskId].map(String),
        };

        result.push(phaseTask);

        task.subTasks?.forEach((subTaskRef) => {
          const subTask = subTaskRef.taskId
            ? subTaskRef
            : allTasks[subTaskRef as unknown as number];

          const taskSubTask = {
            ...frameTaskObject(subTask, index),
            type: 'TASK',
            level: 2,
            parentTotal: task.subTasks.length,
            hierarchy: [
              projectPhase.projectPhaseId,
              task.taskId,
              subTask.taskId,
            ].map(String),
          };

          result.push(taskSubTask);

          subTask.subTasks?.forEach((_subTaskRef) => {
            const _subTask = _subTaskRef.taskId
              ? _subTaskRef
              : allTasks[_subTaskRef as unknown as number];

            const secondLevelSubTask = {
              ...frameTaskObject(_subTask, index),
              parentTotal: subTask.subTasks.length,
              level: 3,
              type: 'TASK',
              hierarchy: [
                projectPhase.projectPhaseId,
                task.taskId,
                subTask.taskId,
                _subTask.taskId,
              ].map(String),
            };

            result.push(secondLevelSubTask);
          });
        });
      });
    });

    const newPhaseId = floatUid();
    const hasNewPhaseRow = _phases?.[_phases?.length - 1]?.isNew;

    if (hasNewPhaseRow) {
      return result;
    }
    result.push({
      key: `PROJECT_PHASE-${newPhaseId}`,
      id: newPhaseId,
      type: 'PROJECT_PHASE',
      level: 0,
      data: {
        isNew: true,
      },
      rootIndex: _phases.length,
      hierarchy: [`${newPhaseId}`],
      insertAt: null,
    });

    return result;
  }, [projectPhaseIds, projectPhases, allTasks, phases]);
};
